/**
 * DifficultyEngine — multi-axis adaptive difficulty.
 *
 * Games describe their tunable knobs as {@link DifficultyAxis} records.
 *   - `tracking: 'performance'` axes auto-adjust based on a rolling success
 *     rate (EWMA) toward `targetSuccessRate`.
 *   - `tracking: 'level'` axes only advance on explicit `nextLevel()` calls —
 *     useful for set-piece progression that should not slide based on a
 *     single tough round.
 *
 * Design biases (in priority order):
 * 1. Bias DOWN-easier. The player population skews older; frustration is
 *    worse than under-challenge. Failures move the dial more than wins, but
 *    are smoothed so a single fluke incorrect doesn't dump the player into
 *    baby mode.
 * 2. Capped step size. No axis can move more than `maxStepUp` (harder) or
 *    `maxStepDown` (easier) per call to `onResult`. The EWMA is the
 *    "memory"; the cap is the "rate limit".
 * 3. Pure, no DOM, no clock — entirely deterministic given inputs. Trivial
 *    to unit-test and to replay against historical event streams.
 */
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
export class DifficultyEngine {
    #axes;
    #target;
    #smoothing;
    #margin;
    #warmup;
    #ewma = null;
    #resultCount = 0;
    constructor(opts) {
        // A game with no auto-adaptive axes is fine; the engine still tracks the
        // success rate so callers can read it via `snapshot()`.
        this.#axes = opts.axes.map((a) => ({ ...a, current: clamp(a.current, a.min, a.max) }));
        this.#target = opts.targetSuccessRate ?? 0.8;
        this.#smoothing = opts.smoothing ?? 0.3;
        this.#margin = opts.margin ?? 0.05;
        this.#warmup = opts.warmupResults ?? 5;
        if (this.#smoothing <= 0 || this.#smoothing > 1) {
            throw new RangeError(`smoothing must be in (0, 1], got ${this.#smoothing}`);
        }
    }
    /**
     * Feed one trial outcome. Updates the rolling success rate and, if the
     * EWMA has drifted outside `target ± margin` AND the engine is past
     * warmup, nudges every `performance`-tracking axis by one capped step.
     */
    onResult(correct) {
        const sample = correct ? 1 : 0;
        this.#ewma =
            this.#ewma === null ? sample : this.#smoothing * sample + (1 - this.#smoothing) * this.#ewma;
        this.#resultCount += 1;
        if (this.#resultCount < this.#warmup)
            return;
        if (this.#ewma === null)
            return;
        const upper = this.#target + this.#margin;
        const lower = this.#target - this.#margin;
        if (this.#ewma > upper) {
            this.#applyDirection(1);
        }
        else if (this.#ewma < lower) {
            this.#applyDirection(-1);
        }
    }
    /**
     * Advance every `level`-tracking axis by one notch (capped to its max).
     * Performance axes are unaffected — `onResult` owns them. Call this from
     * the game when set-piece progression dictates a new level.
     */
    nextLevel() {
        for (const axis of this.#axes) {
            if (axis.tracking !== 'level')
                continue;
            const step = axis.stepUp ?? 1;
            axis.current = clamp(axis.current + step, axis.min, axis.max);
        }
    }
    /** Get the current value of a single axis by name, or undefined. */
    get(name) {
        return this.#axes.find((a) => a.name === name)?.current;
    }
    snapshot() {
        const axes = {};
        for (const a of this.#axes) {
            axes[a.name] = {
                name: a.name,
                value: a.current,
                min: a.min,
                max: a.max,
                tracking: a.tracking,
            };
        }
        return {
            axes,
            successRate: this.#ewma,
            resultCount: this.#resultCount,
        };
    }
    #applyDirection(direction) {
        for (const axis of this.#axes) {
            if (axis.tracking !== 'performance')
                continue;
            const stepUp = axis.stepUp ?? 1;
            const stepDown = axis.stepDown ?? stepUp;
            const desired = direction === 1 ? stepUp : -stepDown;
            const cap = axis.maxStepPerResult ?? Math.max(stepUp, stepDown);
            const change = Math.sign(desired) * Math.min(Math.abs(desired), cap);
            axis.current = clamp(axis.current + change, axis.min, axis.max);
        }
    }
}
//# sourceMappingURL=difficulty.js.map